import Phaser from 'phaser';
import { ScrollablePanel } from '../ui/ScrollablePanel';
import { PUBLIC_ROADMAP, STATUS_INFO, type RoadmapEntry } from '../data/publicRoadmap';
import { GAME_W, GAME_H, GAME_CX } from '../utils/MobileHelper';
import { startSceneWithFade } from '../utils/SceneUtils';
import { FONT_BODY } from '../utils/Typography';
import { addHoverPulse } from '../ui/ButtonPolish';

// === ROADMAP PUBLIQUE IN-GAME ===
// Affiche la vision long terme du jeu de façon visuelle et engageante.
// Style "carnet de bord" cohérent avec l'univers pirate-zombie.
// Ajouté en Phase 10 (v0.39.0-beta).

const HEADER_H = 70;

export class RoadmapScene extends Phaser.Scene {
  private panel!: ScrollablePanel;
  private returnScene = 'BootScene';
  private returnData: Record<string, unknown> = {};

  constructor() {
    super({ key: 'RoadmapScene' });
  }

  init(data: { returnTo?: string } = {}): void {
    this.returnScene = data.returnTo ?? 'BootScene';
    this.returnData = {};
  }

  create(): void {
    this.cameras.main.fadeIn(200, 0, 0, 0);
    this.drawUI();
  }

  private drawUI(): void {
    this.children.removeAll(true);
    // Fond parchemin sombre (sépia teinté nuit)
    this.add.rectangle(GAME_CX, GAME_H / 2, GAME_W, GAME_H, 0x14110a).setDepth(0);

    // Header
    this.add.rectangle(GAME_CX, HEADER_H / 2, GAME_W, HEADER_H, 0x14110a).setDepth(10);
    this.add.text(GAME_CX, 14, '📜 Carnet de Bord du Capitaine', {
      fontSize: '18px', color: '#f1c40f', fontFamily: FONT_BODY, fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(11);
    this.add.text(GAME_CX, 38, 'Ce qui est livré, ce qui arrive, ce dont on rêve.', {
      fontSize: '11px', color: '#bd9a5b', fontFamily: FONT_BODY, fontStyle: 'italic',
    }).setOrigin(0.5).setDepth(11);

    // Bouton retour
    const backBtn = this.add.text(15, 12, '← Retour', {
      fontSize: '11px', color: '#e74c3c', fontFamily: FONT_BODY,
      backgroundColor: '#2c1a1a', padding: { x: 10, y: 6 },
    }).setDepth(12);
    backBtn.setInteractive({ useHandCursor: true });
    backBtn.on('pointerdown', () => startSceneWithFade(this, this.returnScene, this.returnData));
    backBtn.on('pointerover', () => backBtn.setColor('#ff6b6b'));
    backBtn.on('pointerout', () => backBtn.setColor('#e74c3c'));
    addHoverPulse(this, backBtn);

    // Séparateur sous le header (ligne dorée style sceau)
    this.add.rectangle(GAME_CX, HEADER_H, GAME_W, 1, 0xbd9a5b, 0.4).setOrigin(0.5, 0.5).setDepth(10);

    // Contenu scrollable
    this.panel = new ScrollablePanel(this, 0, HEADER_H, GAME_W, GAME_H - HEADER_H);

    let y = 14;
    for (const entry of PUBLIC_ROADMAP) {
      y = this.drawEntry(entry, y);
    }

    // Footnote
    y += 8;
    const footnote = this.add.text(GAME_CX, y,
      'Les dates et la portée évoluent au fil des retours.\nMerci pour votre confiance, capitaine.', {
      fontSize: '10px', color: '#7a6a4f', fontFamily: FONT_BODY,
      align: 'center', fontStyle: 'italic',
    }).setOrigin(0.5, 0).setDepth(1);
    this.panel.add(footnote);
    y += footnote.height + 24;

    this.panel.setContentHeight(y);
  }

  private drawEntry(entry: RoadmapEntry, y: number): number {
    const info = STATUS_INFO[entry.status];
    const cardW = Math.min(560, GAME_W - 20);
    const cardX = GAME_CX;
    const lx = cardX - cardW / 2 + 16;
    const wrapW = cardW - 32;

    // Titre + emoji (ligne 1)
    const title = this.add.text(lx, y + 10, `${entry.emoji}  ${entry.title}`, {
      fontSize: '14px', color: '#ecf0f1', fontFamily: FONT_BODY, fontStyle: 'bold',
    });

    // Pill de statut (à droite du titre, ou dessous si pas la place)
    const isMobile = GAME_W < 600;
    const pill = this.add.text(0, 0, info.label, {
      fontSize: '10px', color: info.color, fontFamily: FONT_BODY, fontStyle: 'bold',
      backgroundColor: '#0a0a14', padding: { x: 8, y: 4 },
    });
    if (isMobile) {
      // Sous le titre, aligné gauche
      pill.setPosition(lx, y + 32);
    } else {
      // À droite du cadre
      pill.setOrigin(1, 0);
      pill.setPosition(cardX + cardW / 2 - 16, y + 12);
    }

    // Date (sous le titre côté gauche)
    const dateY = isMobile ? y + 56 : y + 34;
    const dateText = this.add.text(lx, dateY, `📅 ${entry.date}`, {
      fontSize: '10px', color: '#bd9a5b', fontFamily: FONT_BODY,
    });

    // Résumé (italique, gras)
    const summaryY = dateY + 16;
    const summary = this.add.text(lx, summaryY, entry.summary, {
      fontSize: '11px', color: info.color, fontFamily: FONT_BODY, fontStyle: 'italic bold',
      wordWrap: { width: wrapW },
    });

    // Bullets
    let bulletY = summaryY + summary.height + 8;
    const bulletObjects: Phaser.GameObjects.Text[] = [];
    for (const bullet of entry.bullets) {
      const b = this.add.text(lx + 8, bulletY, `• ${bullet}`, {
        fontSize: '10px', color: '#bdc3c7', fontFamily: FONT_BODY,
        wordWrap: { width: wrapW - 8 },
      });
      bulletObjects.push(b);
      bulletY += b.height + 3;
    }

    const cardH = Math.max(80, bulletY - y + 12);

    // Cadre coloré selon statut (depth 0, derrière les textes)
    const bg = this.add.rectangle(cardX, y + cardH / 2, cardW, cardH,
      parseInt(info.bg.replace('#', ''), 16))
      .setStrokeStyle(2, info.border, 0.85);
    bg.setDepth(0);

    // Reliure du parchemin sur le bord gauche
    const ribbon = this.add.rectangle(cardX - cardW / 2 + 4, y + cardH / 2, 4, cardH - 8, info.border, 0.6);
    ribbon.setDepth(0);

    // Tout ajouter au panel (textes au depth 1 pour passer devant le cadre)
    this.panel.add(bg);
    this.panel.add(ribbon);
    [title, pill, dateText, summary, ...bulletObjects].forEach(t => {
      this.panel.add(t);
      t.setDepth(1);
    });

    return y + cardH + 10;
  }

  shutdown(): void {
    this.panel?.destroy();
  }
}
